import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";

@Component({
  selector: "app-nearby-pros-section",
  templateUrl: "./nearby-pros-section.component.html",
  styleUrls: ["./nearby-pros-section.component.scss"],
})
export class NearbyProsSectionComponent {
  @Input() onlineProsCount: number | null = null;
  @Input() providers: Array<{ user_id?: number; lat?: number; lng?: number }> | null = null;
  @Input()
  cities:
    | Array<{
        nom?: string;
        lat?: number | string;
        lng?: number | string;
        coordinates?: [number, number];
        prestataires_count?: number;
      }>
    | null = null;
  selectedStateName: string | null = null;
  stateIconsWithCounts: Array<{
    name: string;
    left: string;
    top: string;
    delay: string;
    lat: number;
    lng: number;
    activeCount: number;
  }> = [];
  private readonly latMin = 41.25;
  private readonly latMax = 51.4;
  private readonly lngMin = -5.5;
  private readonly lngMax = 9.8;
  private readonly stateIcons = [
    { name: "Hauts-de-France", left: "55.2%", top: "8.3%", delay: "0s", lat: 50.45, lng: 2.8 },
    { name: "Normandie", left: "31.5%", top: "20.5%", delay: "0.15s", lat: 49.1, lng: 0.2 },
    { name: "Ile-de-France", left: "50.6%", top: "25.1%", delay: "0.3s", lat: 48.75, lng: 2.35 },
    { name: "Grand Est", left: "76.6%", top: "21.5%", delay: "0.45s", lat: 48.4, lng: 6.8 },
    { name: "Bretagne", left: "22.4%", top: "31.8%", delay: "0.6s", lat: 48.2, lng: -2.8 },
    { name: "Pays de la Loire", left: "27.5%", top: "40.8%", delay: "0.75s", lat: 47.45, lng: -0.7 },
    { name: "Centre-Val de Loire", left: "49.5%", top: "41.4%", delay: "0.9s", lat: 47.6, lng: 1.8 },
    { name: "Bourgogne-Franche-Comte", left: "66.4%", top: "39.8%", delay: "1.05s", lat: 47.2, lng: 5.7 },
    { name: "Nouvelle-Aquitaine", left: "33.1%", top: "63.4%", delay: "1.2s", lat: 45.3, lng: -0.6 },
    { name: "Auvergne-Rhone-Alpes", left: "66.6%", top: "54.6%", delay: "1.35s", lat: 45.6, lng: 4.7 },
    { name: "Occitanie", left: "44.8%", top: "75.2%", delay: "1.5s", lat: 43.9, lng: 2.0 },
    { name: "Provence-Alpes-Cote d'Azur", left: "75.1%", top: "75.1%", delay: "1.65s", lat: 43.95, lng: 6.0 },
    { name: "Corse", left: "89.0%", top: "89.0%", delay: "1.8s", lat: 42.15, lng: 9.1 },
  ];

  readonly stats = [
    { value: "15k+", labelKey: "UX_TEXT.NEARBY_PROS.STATS.CLIENTS" },
    { value: "2.5k+", labelKey: "UX_TEXT.NEARBY_PROS.STATS.PROS" },
    { value: "50k+", labelKey: "UX_TEXT.NEARBY_PROS.STATS.MISSIONS" },
    { value: "4.8/5", labelKey: "UX_TEXT.NEARBY_PROS.STATS.REVIEWS" },
  ];

  constructor(private router: Router) {}

  get displayCount(): number {
    if (typeof this.onlineProsCount === "number") {
      return this.onlineProsCount;
    }
    return 0;
  }

  get isLoading(): boolean {
    return this.onlineProsCount === null || this.cities === null;
  }

  ngOnInit(): void {
    this.buildDots();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["providers"] || changes["cities"]) {
      this.buildDots();
    }
  }

  private buildDots() {
    const citiesInput = Array.isArray(this.cities) ? this.cities : [];

    const cityPoints = citiesInput
      .map((item) => {
        const lat =
          this.toCoordinate(item?.lat) ||
          this.toCoordinate(Array.isArray(item?.coordinates) ? item.coordinates[1] : undefined);
        const lng =
          this.toCoordinate(item?.lng) ||
          this.toCoordinate(Array.isArray(item?.coordinates) ? item.coordinates[0] : undefined);
        const rawCount = Number(item?.prestataires_count ?? 0);
        return {
          lat,
          lng,
          weight: Number.isFinite(rawCount) && rawCount > 0 ? rawCount : 1,
        };
      })
      .filter(
        (item) =>
          Number.isFinite(item.lat) &&
          Number.isFinite(item.lng) &&
          item.lat >= this.latMin &&
          item.lat <= this.latMax &&
          item.lng >= this.lngMin &&
          item.lng <= this.lngMax
      );

    const validPoints = cityPoints;

    const counts = new Array(this.stateIcons.length).fill(0);
    validPoints.forEach((point) => {
      const nearestIndex = this.getNearestStateIndex(point.lat, point.lng);
      counts[nearestIndex] += point.weight;
    });

    this.stateIconsWithCounts = this.stateIcons.map((state, index) => ({
      ...state,
      activeCount: counts[index],
    }));

    if (this.selectedStateName && !this.stateIconsWithCounts.some((s) => s.name === this.selectedStateName)) {
      this.selectedStateName = null;
    }
  }

  onStateClick(stateName: string) {
    this.selectedStateName = this.selectedStateName === stateName ? null : stateName;
  }

  openMap() {
    (document.activeElement as HTMLElement | null)?.blur();
    this.router.navigate(["/particulier/carte-pros"]);
  }

  private getNearestStateIndex(lat: number, lng: number): number {
    let nearestIndex = 0;
    let minDistance = Number.POSITIVE_INFINITY;

    this.stateIcons.forEach((state, index) => {
      const dLat = lat - state.lat;
      const dLng = lng - state.lng;
      const distance = dLat * dLat + dLng * dLng;
      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  private toCoordinate(value: unknown): number {
    if (typeof value === "number") return value;
    if (typeof value === "string") return Number.parseFloat(value.replace(",", "."));
    return Number.NaN;
  }
}
